import { useParams } from 'react-router-dom';
import { MessageBar, MessageBarBody, makeStyles } from '@fluentui/react-components';

import { GroupDeviceRollup, MigrationGroupRollup } from '../../api/migrationGroups';
import GroupStatusBar from '../../components/GroupStatusBar';
import TenantLabel from '../../components/TenantLabel';
import { Fact, SectionLabel } from '../../components/detailPrimitives';
import { useGroupDetailQuery } from '../../hooks/useGroupDetail';

const useStyles = makeStyles({
  section: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '32px',
  },
  factGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    columnGap: '32px',
    rowGap: '20px',
  },
  barRow: {
    maxWidth: '420px',
    marginTop: '8px',
  },
  destRow: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '12px',
    fontSize: '13px',
    paddingTop: '6px',
    paddingBottom: '6px',
  },
  destCount: {
    color: '#71717a',
    fontSize: '12px',
  },
});

// A count that is genuinely zero renders as 0. A section whose data is ABSENT is
// omitted entirely — the two are different states, and this codebase already
// enforces the distinction (FD-246 notResolved-vs-none). The device breakdown
// below is the case that matters: above the member ceiling the server sends
// `deviceRollup: null` plus a message, and rendering zeros there would report an
// idle group where the truth is "we did not count".
export default function GroupOverviewPage() {
  const styles = useStyles();
  const { groupId } = useParams<{ groupId: string }>();
  const query = useGroupDetailQuery(groupId);

  if (!query.data) return null;

  const { rollup, deviceRollup, deviceRollupOmitted } = query.data;

  return (
    <div className={styles.section}>
      <section>
        <SectionLabel>Migrations</SectionLabel>
        <div className={styles.factGrid}>
          <Fact label="Migrations">{rollup.migrationCount}</Fact>
          <Fact label="Devices assigned">{rollup.deviceCount}</Fact>
          <Fact label="Waves">
            {Object.keys(rollup.byWave).length > 0
              ? Object.keys(rollup.byWave).join(', ')
              : '—'}
          </Fact>
          <Fact label="Types">
            {Object.entries(rollup.byType).length > 0
              ? Object.entries(rollup.byType)
                  .map(([t, n]) => `${t} (${n})`)
                  .join(', ')
              : '—'}
          </Fact>
        </div>
        <div className={styles.barRow}>
          <GroupStatusBar rollup={rollup} />
        </div>
      </section>

      <DestinationsSection rollup={rollup} />

      <section>
        <SectionLabel>Devices</SectionLabel>
        {deviceRollup ? (
          <div className={styles.factGrid}>
            <Fact label="Assigned">{deviceRollup.assigned}</Fact>
            <Fact label="Not started">{deviceRollup.notStarted}</Fact>
            <Fact label="In progress">{deviceRollup.inProgress}</Fact>
            <Fact label="Finished">{deviceRollup.terminal}</Fact>
            {/* Counted IN ADDITION to a lifecycle bucket, so it is presented as a
                separate question rather than beside the buckets it overlaps. */}
            <Fact label="Readiness would hold">{deviceRollup.held}</Fact>
          </div>
        ) : (
          <MessageBar intent="info">
            <MessageBarBody>
              {deviceRollupOmitted ?? 'Device details are not available for this group.'}
            </MessageBarBody>
          </MessageBar>
        )}
        {deviceRollup && Object.values(deviceRollup.byOutcome).some((n) => n > 0) && (
          <OutcomeBreakdown rollup={deviceRollup} />
        )}
      </section>
    </div>
  );
}

function DestinationsSection({ rollup }: { rollup: MigrationGroupRollup }) {
  const styles = useStyles();
  // Omitted rather than shown empty: a group of plain D2C migrations has no
  // named destinations at all (they target the deployment tenant), and an empty
  // "Destinations" heading would read as a lookup failure.
  if (rollup.destinations.length === 0) return null;

  return (
    <section>
      <SectionLabel>Destination tenants</SectionLabel>
      {rollup.destinations.map((d) => (
        <div key={d.tenantId} className={styles.destRow}>
          <TenantLabel tenantId={d.tenantId} domain={d.tenantDomain} />
          <span className={styles.destCount}>
            {d.count} migration{d.count === 1 ? '' : 's'}
          </span>
        </div>
      ))}
    </section>
  );
}

function OutcomeBreakdown({ rollup }: { rollup: GroupDeviceRollup }) {
  const styles = useStyles();
  const entries = Object.entries(rollup.byOutcome).filter(([, n]) => n > 0);
  return (
    <div className={styles.factGrid} style={{ marginTop: '20px' }}>
      {entries.map(([outcome, n]) => (
        <Fact key={outcome} label={outcome}>
          {n}
        </Fact>
      ))}
    </div>
  );
}
